import { clsx } from 'clsx';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type {
  AuditEvent,
  BenefitWrite,
  RawResponseRef,
  Verification,
} from '../api/verificationApi';
import { formatMoney, formatPercent, formatTimestamp } from '../utils/format';
import styles from './AuditTrailTab.module.css';

interface AuditTrailTabProps {
  verification: Verification;
  audit: AuditEvent[];
  rawResponses: RawResponseRef[];
  benefitWrites: BenefitWrite[];
}

// ─── Payload readers (payload is untyped on the wire) ────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(payload: unknown, key: string): string | null {
  if (!isRecord(payload)) return null;
  const v = payload[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

interface WriteRow {
  sourceField: string;
  benefitType: string | null;
  amount: number | null;
  percent: number | null;
}

interface AaaLine {
  code: string | null;
  description: string;
  followupAction: string | null;
  possibleResolutions: string | null;
}

function readAaa(payload: unknown): AaaLine[] {
  if (!isRecord(payload) || !Array.isArray(payload.aaa)) return [];
  return payload.aaa.flatMap((a): AaaLine[] => {
    if (!isRecord(a)) return [];
    return [
      {
        code: typeof a.code === 'string' ? a.code : null,
        description: typeof a.description === 'string' ? a.description : '',
        followupAction: typeof a.followupAction === 'string' ? a.followupAction : null,
        possibleResolutions:
          typeof a.possibleResolutions === 'string' ? a.possibleResolutions : null,
      },
    ];
  });
}

function readRows(payload: unknown): WriteRow[] {
  if (!isRecord(payload) || !Array.isArray(payload.rows)) return [];
  return payload.rows.flatMap((r): WriteRow[] => {
    if (!isRecord(r)) return [];
    const sourceField = typeof r.sourceField === 'string' ? r.sourceField : null;
    if (!sourceField) return [];
    const benefitType =
      typeof r.BenefitType === 'string'
        ? r.BenefitType
        : typeof r.benefitType === 'string'
          ? r.benefitType
          : null;
    const amount =
      typeof r.MonetaryAmt === 'number'
        ? r.MonetaryAmt
        : typeof r.amount === 'number'
          ? r.amount
          : null;
    const percent =
      typeof r.Percent === 'number' ? r.Percent : typeof r.percent === 'number' ? r.percent : null;
    return [{ sourceField, benefitType, amount, percent }];
  });
}

function eventTitle(event: AuditEvent, carrierName: string): string {
  switch (event.type) {
    case 'created':
      return 'Verification created';
    case 'import':
      return 'Imported from schedule';
    case 'electronic_check':
      return `Electronic check — ${carrierName} (via Stedi)`;
    case 'electronic_failed':
      return `Electronic check failed — ${carrierName}`;
    case 'awaiting_production':
      return 'Held until Stedi is in production';
    case 'review_approved':
      return 'Reviewer sign-off';
    case 'review_flagged':
      return 'Flagged for review';
    case 'opendental_write':
      return 'Written to Open Dental';
    case 'opendental_dry_run':
      return 'Prepared for Open Dental (dry run)';
    case 'call_queued':
      return 'Payer call queued';
    case 'phi_revealed':
      return 'Date of birth viewed';
    default:
      return event.summary;
  }
}

function isPendingNode(type: AuditEvent['type']) {
  return type === 'call_queued' || type === 'awaiting_production';
}

export function AuditTrailTab({
  verification,
  audit,
  rawResponses,
  benefitWrites,
}: AuditTrailTabProps) {
  const carrier = verification.plan.carrierName;

  return (
    <div className={styles.root}>
      <h3 className={styles.sectionTitle}>Every step is logged for claim disputes</h3>

      {audit.length === 0 ? (
        <p className={patterns.cellMuted}>Nothing logged yet.</p>
      ) : (
        <ol className={styles.timeline}>
          {audit.map((event) => {
            const summary =
              readString(event.payload, 'summary') ??
              (event.type === 'electronic_failed' ? readString(event.payload, 'message') : null);
            const trace =
              readString(event.payload, 'externalId') ?? readString(event.payload, 'traceId');
            const rows = readRows(event.payload);
            const aaa = event.type === 'electronic_failed' ? readAaa(event.payload) : [];
            const technical =
              event.type === 'electronic_failed' ? readString(event.payload, 'technical') : null;
            const showSummary =
              summary && (event.type === 'electronic_check' || event.type === 'electronic_failed');
            const isWrite =
              event.type === 'opendental_write' || event.type === 'opendental_dry_run';

            return (
              <li key={event.id} className={styles.item}>
                <span
                  className={clsx(styles.node, !isPendingNode(event.type) && styles.nodeDone)}
                  aria-hidden
                />
                <div className={styles.title}>{eventTitle(event, carrier)}</div>
                <div className={styles.meta}>
                  {formatTimestamp(event.createdAt)}
                  {event.summary && !showSummary && ` · ${event.summary}`}
                  {trace && ` · trace ${trace}`}
                </div>
                {showSummary && (
                  <div className={styles.box}>
                    {summary}
                    {aaa.length > 0 && (
                      <ul className={styles.rowList}>
                        {aaa.map((a, i) => (
                          <li key={`${a.code}-${i}`}>
                            <span className={styles.mono}>
                              {a.code ? `AAA ${a.code}` : 'error'}
                            </span>{' '}
                            {a.description}
                            {a.followupAction && ` — ${a.followupAction}`}
                            {a.possibleResolutions && ` · ${a.possibleResolutions}`}
                          </li>
                        ))}
                      </ul>
                    )}
                    {technical && technical !== summary && (
                      <div className={styles.mono}>{technical}</div>
                    )}
                  </div>
                )}
                {isWrite && rows.length > 0 && (
                  <div className={styles.box}>
                    <ul className={styles.rowList}>
                      {rows.map((r, i) => (
                        <li key={`${r.sourceField}-${i}`}>
                          <span className={styles.mono}>{r.sourceField}</span>
                          {r.benefitType && ` · ${r.benefitType}`}
                          {r.amount != null && ` · ${formatMoney(r.amount)}`}
                          {r.percent != null && ` · ${formatPercent(r.percent)}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {rawResponses.length > 0 && (
        <section>
          <h3 className={styles.sectionTitle}>Raw payer responses</h3>
          <div className={patterns.gridTable}>
            <div className={clsx(patterns.gridTableHeader, styles.rawCols)}>
              <span>Kind</span>
              <span>Trace</span>
              <span>Received</span>
            </div>
            {rawResponses.map((r) => (
              <div key={r.id} className={clsx(patterns.gridTableRow, styles.rawCols)}>
                <div className={patterns.cellSecondary}>{r.kind}</div>
                <div className={clsx(patterns.cellSecondary, styles.mono)}>
                  {r.externalId ?? r.id}
                </div>
                <div className={patterns.cellMuted}>{formatTimestamp(r.createdAt)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {benefitWrites.length > 0 && (
        <section>
          <h3 className={styles.sectionTitle}>Benefit rows sent to Open Dental</h3>
          <div className={patterns.gridTable}>
            <div className={clsx(patterns.gridTableHeader, styles.writeCols)}>
              <span>Field</span>
              <span>Mode</span>
              <span>BenefitNum</span>
              <span>When</span>
            </div>
            {benefitWrites.map((w, i) => (
              <div
                key={`${w.sourceField}-${i}`}
                className={clsx(patterns.gridTableRow, styles.writeCols)}
              >
                <div className={clsx(patterns.cellSecondary, styles.mono)}>{w.sourceField}</div>
                <div>
                  <span className={w.mode === 'live' ? patterns.pillSuccess : patterns.pillMuted}>
                    {w.mode === 'live' ? 'Live' : 'Dry run'}
                  </span>
                </div>
                <div className={patterns.cellMuted}>{w.benefitNum ?? '—'}</div>
                <div className={patterns.cellMuted}>{formatTimestamp(w.createdAt)}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
